import { observer } from "mobx-react-lite";
import React, { useContext, useEffect } from "react";

import CollapsableImageContent from "../components/CollapsableImageContent";
import EstimateTable from "../components/EstimateTable";
import FloorplanCustomiser from "../components/FloorplanCustomiser";
import GoogleMap from "../components/GoogleMap";
import Icon from "../components/Icon";
import PromotionBanner from "../components/PromotionBanner";
import YourNewHomeFooter from "../components/YourNewHomeFooter";
import { ApiContext,NavigationContext,UserContext } from "../contexts";
import {
  FloorplanOption,
  HouseLand,
  IconName,
  LandType,
} from "../types";
import { scrollToTop } from "../util";
import Rendivo from "../util/rendivo";

const YourNewHomeScreen = observer(() => {
  const userContext = useContext(UserContext);
  const navContext = useContext(NavigationContext);
  const apiContext = useContext(ApiContext);

  useEffect(() => {
    scrollToTop();
    navContext.resetNextBtn();
    navContext.resetOnNavigateBack();
    navContext.setFooterNavVisible(true);
    if(userContext.isKioskMode){
      navContext.setNextButtonVisible(true);
    }else{
      navContext.setNextButtonVisible(false);
    }
    navContext.setNextButtonEnabled(true);
    navContext.setBackButtonVisible(false);
    navContext.setSubtotalVisible(false);
  }, []);

  const getInteriorStylesRenders = () => {
    const selectedInterior = userContext.selectedInteriorStyle;
    const renders: { type: string; image: string }[] = [];
    if (selectedInterior) {
      const selectedKitchenStyle = userContext.selectedKitchenStyle.get(
        selectedInterior.id
      );
      const selectedFlooringStyle = userContext.selectedFlooringStyle.get(
        selectedInterior.id
      );

      selectedInterior.slider_items.map((item) => {
        let image = "";
        if (item.rendivo_id !== null) {
          // Inclusions can override the kitchen image, need to support that here.
          let rendivoProductId = item.rendivo_id;
          let kitchenId = "";
          let flooringId = "";
          if (
            item.type === "kitchen" &&
            userContext.selectedInclusion &&
            userContext.selectedInclusion.kitchen_rendivo_id
          ) {
            rendivoProductId = userContext.selectedInclusion.kitchen_rendivo_id;
          }

          if (selectedKitchenStyle) {
            kitchenId = selectedKitchenStyle.rendivo_id;
          }
          if (selectedFlooringStyle) {
            flooringId = selectedFlooringStyle.rendivo_id;
          }

          image = Rendivo.getExteriorRender(
            1640,
            rendivoProductId,
            null,
            selectedInterior.rendivo_id,
            kitchenId,
            flooringId,
            "",
            "",
            false
          );
        }else if(item.image !== null){
          image = item.image;
        }
        renders.push({
          type: item.type,
          image,
        });
      });
    }

    return renders;
  };

  const isHouseLand = userContext.landType === LandType.HOUSE_LAND_PACKAGE;

  let selectedFloorplanOptions: FloorplanOption[] = [];
  if (!isHouseLand && userContext.selectedHome) {
    selectedFloorplanOptions =
      userContext.selectedFloorplanOptions.get(userContext.selectedHome.id) ||
      [];
  } else if (isHouseLand && userContext.selectedHouseLand) {
    selectedFloorplanOptions =
      userContext.selectedFloorplanOptions.get(
        userContext.selectedHouseLand.id
      ) || [];
  }

  let facadeTitle = "";
  let finishTitle = "";
  if (isHouseLand) {
    const home = userContext.selectedHouseLand;
    if (home) {
      facadeTitle = home.facade_title;
      finishTitle = home.facade_finish;
    }
  } else if (userContext.selectedFacade) {
    const selectedFinish = userContext.selectedFacadeFinishOption.get(
      userContext.selectedFacade.id
    );
    facadeTitle = userContext.selectedFacade.title;
    finishTitle = selectedFinish ? selectedFinish.label : "";
  }

  let selectedKitchenStyle;
  let selectedFlooringStyle;
  if (userContext.selectedInteriorStyle) {
    selectedKitchenStyle = userContext.selectedKitchenStyle.get(
      userContext.selectedInteriorStyle.id
    );
    selectedFlooringStyle = userContext.selectedFlooringStyle.get(
      userContext.selectedInteriorStyle.id
    );
  }

  const promos = [];
  if (userContext.selectedPromotion) {
    promos.push(userContext.selectedPromotion);
  }
  if (userContext.selectedSpecialOffer) {
    promos.push(userContext.selectedSpecialOffer);
  }

  let selectedRoofStyle;
  if(userContext.selectedExteriorStyle){
    selectedRoofStyle = userContext.selectedRoofStyle.get(
      userContext.selectedExteriorStyle.id
    );
  }

  return (
    <div
      className={`screen screen-your-new-home land-type-${
        userContext.landType
      } ${userContext.isMyDomaineMode ? "my-domaine-mode" : ""} ${
        userContext.isShareMode ? "share-mode" : ""
      }`}
    >
      {apiContext.finalDetailsSettings.print_logo && (
        <div className="print-logo">
          <img
            src={apiContext.finalDetailsSettings.print_logo}
            alt={""}
          />
        </div>
      )}
      {!userContext.isShareOrMyDomaineMode && (
        <div className={"container"}>
          {isHouseLand ? (
            <h2 className="h1">Presenting Your New House and Land Package.</h2>
          ) : (
            <h2 className="h1">Presenting Your New Home.</h2>
          )}
          <p className={"subtitle"}>
            {userContext.firstName ? userContext.firstName + ", we" : "We"} are pleased to present you with your new
            family home.
          </p>
        </div>
      )}

      <div className={"container section section-exterior"}>
        <div
          className={`image-container ${
            !!userContext.selectedHome && userContext.selectedHome.narrow
              ? "narrow"
              : ""
          } ${
            !!userContext.selectedHome && userContext.selectedHome.acreage
              ? "acreage"
              : ""
          } ${
            !!userContext.selectedFacade &&
            userContext.selectedFacade.rendivo_levels > 1
              ? "double-story"
              : ""
          }`}
        >
          <img
            className={"exterior-render"}
            src={userContext.rendivoExteriorRender}
            srcSet={Rendivo.getSrcsetFromRendivoUrl(userContext.rendivoExteriorRender, 1640)}
            sizes="(max-width: 1640px) 100vw, 1640px"
            alt={""}
          />
        </div>

        {userContext.isShareOrMyDomaineMode && (
          <>
            {!isHouseLand && userContext.selectedHome && (
              <h2 className={"my-domain-title"}>
                {userContext.selectedHome.title}
              </h2>
            )}
            {isHouseLand && userContext.selectedHouseLand && (
              <h2 className={"my-domain-title"}>
                {userContext.selectedHouseLand.house_design}
              </h2>
            )}
            <p className={"my-domaine-subtotal"}>
              ${userContext.totalPriceWithSiteWorks.toLocaleString()}
            </p>
          </>
        )}

        <div className={"detail-pills"}>
          <p className={"detail-pill"}>Facade : {facadeTitle}</p>
          <p className={"detail-pill"}>Finish : {finishTitle}</p>
          <p className={"detail-pill"}>
            Exterior Colour Scheme :{" "}
            {userContext.selectedExteriorStyle &&
              userContext.selectedExteriorStyle.title}
          </p>
          <p className={"detail-pill"}>
            Roof :{" "}
            {selectedRoofStyle &&
              selectedRoofStyle.title}
          </p>
        </div>
      </div>

      {isHouseLand && userContext.selectedHouseLand !== null && (
        <div className={"container section section-house-land"}>
          <h2>
            {userContext.selectedHouseLand.address}{" "}
            {userContext.selectedHouseLand.suburb}{" "}
            {userContext.selectedHouseLand.state}{" "}
            {userContext.selectedHouseLand.postcode}
          </h2>

          <div className={"house-land-details"}>
            <div className={"map-container"}>
              <GoogleMap
                id={userContext.selectedHouseLand.id}
                latitude={userContext.selectedHouseLand.latitude}
                longitude={userContext.selectedHouseLand.longitude}
              />
            </div>
            <div className={"details-section"}>
              <div className={"pricing"}>
                <p className={"section-label"}>
                  {userContext.selectedHouseLand.pdf_fixed_price
                    ? "Fixed Price"
                    : " "}
                </p>
                <p className={"price"}>
                  ${userContext.subtotalPrice.toLocaleString()}*
                </p>
              </div>
              <div className={"details-container"}>
                <div className={"icons-row"}>
                  <div className={"icons-item"}>
                    <Icon name={IconName.BED} />
                    <span>{userContext.selectedHouseLand.bedrooms}</span>
                  </div>
                  <div className={"icons-item"}>
                    <Icon name={IconName.COUCH} />
                    <span>{userContext.selectedHouseLand.living_areas}</span>
                  </div>
                  <div className={"icons-item"}>
                    <Icon name={IconName.BATH} />
                    <span>{userContext.selectedHouseLand.bathrooms}</span>
                  </div>
                  <div className={"icons-item"}>
                    <Icon name={IconName.CAR} />
                    <span>{userContext.selectedHouseLand.car_spaces}</span>
                  </div>
                </div>
                <div className={"details-cols"}>
                  <div className={"details"}>
                    <div className={"details-row"}>
                      <span className={"label"}>House Design</span>
                      <span className={"separator"}>:</span>
                      <span className={"value"}>
                        {userContext.selectedHouseLand.house_design}
                      </span>
                    </div>
                    <div className={"details-row"}>
                      <span className={"label"}>Facade</span>
                      <span className={"separator"}>:</span>
                      <span className={"value"}>
                        {userContext.selectedHouseLand.facade_title}
                      </span>
                    </div>
                    <div className={"details-row"}>
                      <span className={"label"}>Facade Finish</span>
                      <span className={"separator"}>:</span>
                      <span className={"value"}>
                        {userContext.selectedHouseLand.facade_finish}
                      </span>
                    </div>
                    <div className={"details-row"}>
                      <span className={"label"}>Exterior Colour Scheme</span>
                      <span className={"separator"}>:</span>
                      <span className={"value"}>
                        {userContext.selectedHouseLand.exterior_colour}
                      </span>
                    </div>
                    {userContext.selectedInteriorStyle && (
                      <div className={"details-row"}>
                        <span className={"label"}>Interior Colour Scheme</span>
                        <span className={"separator"}>:</span>
                        <span className={"value"}>
                          {userContext.selectedInteriorStyle.title}
                        </span>
                      </div>
                    )}
                    <div className={"details-row"}>
                      <span className={"label"}>Lot Size</span>
                      <span className={"separator"}>:</span>
                      <span className={"value"}>
                        {userContext.selectedHouseLand.lot_size}m&sup2;
                      </span>
                    </div>
                    <div className={"details-row"}>
                      <span className={"label"}>House Size</span>
                      <span className={"separator"}>:</span>
                      <span className={"value"}>
                        {userContext.selectedHouseLand.total_area_m}m&sup2; (
                        {userContext.selectedHouseLand.total_area_sq}
                        sq)
                      </span>
                    </div>
                    <div className={"details-row"}>
                      <span className={"label"}>House Width</span>
                      <span className={"separator"}>:</span>
                      <span className={"value"}>
                        {userContext.selectedHouseLand.width}m
                      </span>
                    </div>
                    <div className={"details-row"}>
                      <span className={"label"}>House Length</span>
                      <span className={"separator"}>:</span>
                      <span className={"value"}>
                        {userContext.selectedHouseLand.length}m
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={"container"}>
        <h2 className={"h1"}>Floorplan</h2>
      </div>
      <div className={"container section section-floorplan"}>
        {!isHouseLand && userContext.selectedHome && (
          <FloorplanCustomiser
            readonly={true}
            home={userContext.selectedHome}
            isFloorplanFlipped={
              userContext.isFloorplanFlipped.get(userContext.selectedHome.id) ||
              false
            }
            isActive={false}
            selectedOptions={selectedFloorplanOptions}
            isHouseLand={false}
          />
        )}
        {isHouseLand && userContext.selectedHouseLand && (
          <FloorplanCustomiser
            readonly={true}
            home={userContext.selectedHouseLand}
            isFloorplanFlipped={
              userContext.isFloorplanFlipped.get(
                userContext.selectedHouseLand.id
              ) || false
            }
            isActive={false}
            selectedOptions={selectedFloorplanOptions}
            isHouseLand={true}
          />
        )}
      </div>
      <div className="pagebreak"></div>
      <div className={"container flex-title"}>
        <h2 className={"h1"}>Interior Colours</h2>
        {userContext.selectedInteriorStyle && (
          <div className={"detail-pills"}>
            <p className={"detail-pill"}>
              Interior Colour Scheme : {userContext.selectedInteriorStyle.title}
            </p>
          </div>
        )}
      </div>
      {userContext.selectedInteriorStyle && (
        <div className={`container section section-interior-colours images-${getInteriorStylesRenders().length}`}>
          {getInteriorStylesRenders().map((item) => {
            return (
              <div className={`image ${item.type}`} key={item.type}>
                <div className={"image-container"}>
                  <img src={item.image} alt={""} />
                </div>
              </div>
            );
          })}
          <div className="disclaimer" key="disclaimer">*For illustrative purposes only.</div>
        </div>
      )}
      {/*userContext.selectedInclusion && (
        <>
          <div className={"container"}>
            <h2 className={"h1"}>Inclusions</h2>
          </div>
          <div className={"container section section-inclusions"}>
            <CollapsableImageContent
              readonly={true}
              layout={"your_home"}
              image={userContext.selectedInclusion.image}
              title={userContext.selectedInclusion.title}
              price={userContext.selectedInclusion.price}
              promo_price={userContext.selectedInclusion.promo_price}
              selected={false}
              selectableHidden={true}
              insetTick={true}
              innerHtml={userContext.selectedInclusion.content}
            />
          </div>
          <div className="pagebreak"></div>
        </>
      )*/}

      {promos.length > 0 && (
        <>
          <div className={"container"}>
            <h2 className={"h1"}>Special Offers</h2>
          </div>
          <div className={"container section section-offers"}>
            {promos.map((item) => {
              return (
                <PromotionBanner
                  key={item.id}
                  promotion={item}
                  selected={false}
                  selectableHidden={true}
                  showPopout={false}
                />
              );
            })}
          </div>
          <div className="pagebreak"></div>
        </>
      )}

      <div className={"container section-estimate-table"}>
        {isHouseLand ? (
          <>
            <h2 className={"h1 house-land-title"}>Home and Land</h2>
            {userContext.selectedHouseLand && (
              <p>
                {userContext.selectedHouseLand.address}{" "}
                {userContext.selectedHouseLand.suburb}{" "}
                {userContext.selectedHouseLand.state}{" "}
                {userContext.selectedHouseLand.postcode}
              </p>
            )}
          </>
        ) : (
          <h2 className={"h1"}>New Home Estimate</h2>
        )}
        {!isHouseLand && userContext.selectedHome && (
          <EstimateTable
            home={userContext.selectedHome as HouseLand}
            isHouseLand={false}
          />
        )}
        {isHouseLand && userContext.selectedHouseLand && (
          <EstimateTable
            home={userContext.selectedHouseLand}
            isHouseLand={true}
          />
        )}
      </div>
      {isHouseLand && (
        <div className={"container section-further-customise"}>
          <p className={"bold"}>
            Talk to a Domaine consultant about securing this package
          </p>
        </div>
      )}

      <YourNewHomeFooter
        isHouseLand={isHouseLand}
        isShareOrMyDomaineMode={userContext.isShareOrMyDomaineMode}
      />
    </div>
  );
});

export default YourNewHomeScreen;
